import { defineStore } from "pinia"
import { ref, computed, watch } from "vue"
import { DEFAULT_BRAND_COLOR, normalizeBrandColor, brandForeground, brandPalette } from "@/utils/brandColor"
import { useThemeStore } from "@/stores/theme"

interface PersistedBrand {
  color: string
  logoUrl: string
  name: string
}

// Kept across logouts on purpose: the login page is shown before config exists, and a machine that has
// been logged in before should meet its taxi system's logo and colour from the first frame
const BRAND_STORAGE_KEY = "pakketransport.brand"

function loadPersisted(): PersistedBrand | null {
  try {
    const raw = localStorage.getItem(BRAND_STORAGE_KEY)
    return raw ? (JSON.parse(raw) as PersistedBrand) : null
  } catch {
    return null
  }
}

export const useBrandStore = defineStore("brand", () => {
  const persisted = loadPersisted()
  const themeStore = useThemeStore()

  const color = ref(normalizeBrandColor(persisted?.color ?? DEFAULT_BRAND_COLOR))
  const logoUrl = ref(persisted?.logoUrl ?? "")
  const name = ref(persisted?.name ?? "")
  // The API sends a logo URL whether or not the file exists (HFT, ITA and TOS are missing in S3)
  const logoFailed = ref(false)

  const foreground = computed(() => brandForeground(color.value))
  const showLogo = computed(() => !!logoUrl.value && !logoFailed.value)
  // Nothing known yet: a browser that has never been logged in, before the first config
  const known = computed(() => !!logoUrl.value || !!name.value)
  // Every taxi logo is drawn for a light background except ITF's, which is light grey for a dark header
  const logoForDark = computed(() => /\/itf\.png$/i.test(logoUrl.value))

  // The app's greys, its main action and the brand's own colour all follow the taxi system, per theme
  function apply(): void {
    const root = document.documentElement
    for (const [name, value] of Object.entries(brandPalette(color.value, themeStore.isDark))) root.style.setProperty(name, value)
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", color.value)
  }

  function setFromConfig(taxiSystem: { primaryColor?: string; logoImageUrl?: string } | undefined, taxiTeamName: string | undefined): void {
    color.value = normalizeBrandColor(taxiSystem?.primaryColor)
    logoUrl.value = taxiSystem?.logoImageUrl ?? ""
    name.value = taxiTeamName ?? ""
    try {
      const snapshot: PersistedBrand = { color: color.value, logoUrl: logoUrl.value, name: name.value }
      localStorage.setItem(BRAND_STORAGE_KEY, JSON.stringify(snapshot))
    } catch {}
  }

  function markLogoFailed(): void {
    logoFailed.value = true
  }

  watch(logoUrl, () => (logoFailed.value = false))
  watch([color, () => themeStore.isDark], apply, { immediate: true })

  return {
    color,
    logoUrl,
    name,
    foreground,
    showLogo,
    known,
    logoForDark,
    setFromConfig,
    markLogoFailed
  }
})
